import {useNavigationBlocker} from '@hconnect/common/hooks'
import {DepartmentNode, TreeNode, UpmEntityType} from '@hconnect/common/types'
import {useNotification} from '@hconnect/uikit/src/common'
import {icon} from '@hm/fundamental'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ConfirmationModal} from '../../../common/components/ConfirmationModal'
import {usePlantConfig} from '../../../common/hooks/usePlantConfig'
import {
  CRUSHER_FLOW_CONFIG_NAME,
  CRUSHER_TERMINAL_NODE,
  MAX_CRUSHER_FLOW_SETUPS,
  crusherValidator
} from '../../consts/crushers'
import {
  FlowCanvas,
  FlowTabs,
  FlowScenariosTable,
  buildFlowScenarios,
  useFlowConfig
} from '../../flow'
import {mapToFlowEntities} from '../../helpers'
import {ItemDetailsLayout} from '../../plant-setup-item-layout'
import {useSelectedItem} from '../../providers/SelectedItemProvider'

import {CrusherFlowInfoBanner} from './CrusherFlowInfoBanner'

interface CrusherFlowProps {
  selectedItem: TreeNode
}

export const CrusherFlowContainer: React.FC<CrusherFlowProps> = ({selectedItem}) => {
  const {upmPlantId, selectionGuardRef} = useSelectedItem()
  const {t} = useTranslation()
  const {notify} = useNotification()

  const {data: plantConfigs} = usePlantConfig(upmPlantId, true)

  // cast via unknown: removeProductionLinesFromTree flattens the tree, so area children are DepartmentNodes at runtime
  const crusherNodes = useMemo(
    () =>
      selectedItem.children.filter(
        (node) => node.type?.name === 'Crusher'
      ) as unknown as DepartmentNode[],
    [selectedItem]
  )

  const entities = useMemo(
    () => mapToFlowEntities(crusherNodes, plantConfigs),
    [crusherNodes, plantConfigs]
  )

  const {
    config,
    activeSetup,
    selectedSetupId,
    setSelectedSetupId,
    isDirty,
    handleSave,
    handleRevert,
    handleCreateSetup,
    handleDeleteSetup,
    handleLinkAdded,
    handleLinkRemoved,
    handleNodeMoved,
    canCreateSetup
  } = useFlowConfig({
    plantUpmId: upmPlantId,
    entityUpmId: selectedItem.upmId,
    entityType: UpmEntityType.Area,
    configName: CRUSHER_FLOW_CONFIG_NAME,
    entities,
    terminalNode: CRUSHER_TERMINAL_NODE,
    maxSetups: MAX_CRUSHER_FLOW_SETUPS
  })

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showRevertModal, setShowRevertModal] = useState(false)

  const {isBlocked, handleClose, setIsBlockActive} = useNavigationBlocker(isDirty)

  useEffect(() => {
    selectionGuardRef.current = isDirty
    return () => {
      selectionGuardRef.current = false
    }
  }, [isDirty, selectionGuardRef])

  const getSetupLabel = useCallback(
    (index: number) =>
      index === 0
        ? t('plantSetup.crusherFlowSetup.coreSetup')
        : t('plantSetup.crusherFlowSetup.setupName', {number: index + 1}),
    [t]
  )

  const isCoreSetupSelected = selectedSetupId === 'setup-1'

  const setupLabels = useMemo(
    () => config.setups.map((_, i) => getSetupLabel(i)),
    [config.setups, getSetupLabel]
  )
  const {setups} = config
  const scenarios = useMemo(
    () => buildFlowScenarios(setups, entities, CRUSHER_TERMINAL_NODE, setupLabels),
    [setups, entities, setupLabels]
  )

  const onCreateSetup = () => {
    if (handleCreateSetup()) {
      notify('success', t('plantSetup.crusherFlowSetup.toast.setupCreated'))
    }
  }

  return (
    <ItemDetailsLayout
      revertButton={{
        hide: false,
        onClick: () => setShowRevertModal(true),
        disabled: !isDirty
      }}
      saveButton={{
        hide: false,
        onClick: () => void handleSave(),
        disabled: !isDirty
      }}
    >
      <div className="flex flex-col gap-6">
        <CrusherFlowInfoBanner />

        <FlowTabs
          setups={config.setups}
          selectedSetupId={selectedSetupId}
          onSetupSelect={setSelectedSetupId}
          onCreateSetup={onCreateSetup}
          onDeleteSetup={() => setShowDeleteModal(true)}
          canCreateSetup={canCreateSetup}
          canDeleteSetup={!isCoreSetupSelected}
          getSetupLabel={getSetupLabel}
          createLabel={t('plantSetup.crusherFlowSetup.createNewSetup')}
          deleteLabel={t('plantSetup.crusherFlowSetup.deleteSetup')}
          limitReachedLabel={t('plantSetup.crusherFlowSetup.setupLimitReached')}
        />

        <FlowCanvas
          setup={activeSetup}
          entities={entities}
          terminalNode={CRUSHER_TERMINAL_NODE}
          validator={crusherValidator}
          onLinkAdded={handleLinkAdded}
          onLinkRemoved={handleLinkRemoved}
          onNodeMoved={handleNodeMoved}
          onConnectionRejected={(reason) => {
            if (reason === 'duplicate') return
            notify(
              'warning',
              t(
                reason === 'maxDepth'
                  ? 'plantSetup.crusherFlowSetup.toast.maxDepthReached'
                  : 'plantSetup.crusherFlowSetup.toast.circularConnection'
              )
            )
          }}
        />

        <FlowScenariosTable
          scenarios={scenarios}
          scenariosLabel={t('plantSetup.crusherFlowSetup.scenarios')}
          setupLabel={t('plantSetup.crusherFlowSetup.setup')}
        />
      </div>

      <ConfirmationModal
        open={isBlocked}
        title={t('plantSetup.crusherFlowSetup.saveBeforeLeaving')}
        message={t('plantSetup.crusherFlowSetup.unsavedChangesWillBeLost')}
        onClose={handleClose}
        cancelButton={{
          text: t('plantSetup.crusherFlowSetup.cancel'),
          icon: icon.mdiClose,
          onClick: handleClose
        }}
        secondaryButton={{
          text: t('plantSetup.crusherFlowSetup.leaveAnyway'),
          icon: icon.mdiExitRun,
          onClick: () => {
            setIsBlockActive(false)
            handleClose()
          }
        }}
        confirmButton={{
          text: t('plantSetup.crusherFlowSetup.save'),
          icon: icon.mdiContentSave,
          onClick: () => {
            void handleSave().then(() => {
              setIsBlockActive(false)
              handleClose()
            })
          }
        }}
        data-test-id="unsaved-changes-modal"
      />

      <ConfirmationModal
        open={showDeleteModal}
        title={t('plantSetup.crusherFlowSetup.deleteSetupTitle')}
        message={t('plantSetup.crusherFlowSetup.deleteSetupMessage')}
        variant="destructive"
        onClose={() => setShowDeleteModal(false)}
        cancelButton={{
          text: t('plantSetup.crusherFlowSetup.cancel'),
          icon: icon.mdiClose,
          onClick: () => setShowDeleteModal(false)
        }}
        confirmButton={{
          text: t('plantSetup.crusherFlowSetup.removeSetup'),
          icon: icon.mdiDeleteOutline,
          onClick: () => {
            handleDeleteSetup(selectedSetupId)
            setShowDeleteModal(false)
            notify('success', t('plantSetup.crusherFlowSetup.toast.setupDeleted'))
          }
        }}
        data-test-id="delete-setup-modal"
      />

      <ConfirmationModal
        open={showRevertModal}
        title={t('plantSetup.crusherFlowSetup.revertChangesTitle')}
        message={t('plantSetup.crusherFlowSetup.revertChangesMessage')}
        onClose={() => setShowRevertModal(false)}
        cancelButton={{
          text: t('plantSetup.crusherFlowSetup.cancel'),
          icon: icon.mdiClose,
          onClick: () => setShowRevertModal(false)
        }}
        confirmButton={{
          text: t('plantSetup.crusherFlowSetup.revert'),
          icon: icon.mdiRestore,
          onClick: () => {
            handleRevert()
            setShowRevertModal(false)
            notify('success', t('plantSetup.crusherFlowSetup.toast.setupReverted'))
          }
        }}
        data-test-id="revert-changes-modal"
      />
    </ItemDetailsLayout>
  )
}
